import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Check, X, Crown, Inbox, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const statusLabel = { pending: "In attesa", accepted: "Accettata", rejected: "Rifiutata" };
const statusClass = {
  pending: "border-[hsl(var(--gold))] text-[hsl(30_60%_40%)]",
  accepted: "bg-success text-white border-0",
  rejected: "border-destructive text-destructive",
};

export default function Requests() {
  const navigate = useNavigate();
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);

  const load = async () => {
    const [r, s] = await Promise.all([api.get("/requests/received"), api.get("/requests/sent")]);
    setReceived(r.data); setSent(s.data);
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try { await api.post(`/requests/${id}/${action}`); toast.success(action === "accept" ? "Richiesta accettata" : "Richiesta rifiutata"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const Row = ({ r, mine }) => (
    <div className="rounded-2xl border border-border/60 p-4 flex items-center gap-4" data-testid={`request-row-${r.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate flex items-center gap-2">{r.listing_title}
          {r.priority && <Badge className="bg-[hsl(var(--gold))] text-white border-0 text-[10px]"><Crown className="h-3 w-3 mr-0.5" /> Priorità</Badge>}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">{mine ? "La tua richiesta" : `Da ${r.requester_name}`}</div>
      </div>
      <Badge variant="outline" className={statusClass[r.status]}>{statusLabel[r.status]}</Badge>
      <Button size="sm" variant="ghost" className="rounded-full" onClick={() => navigate(`/chat/${r.id}`)} data-testid={`chat-btn-${r.id}`}><MessageSquare className="h-4 w-4" /></Button>
      {!mine && r.status === "pending" && (
        <div className="flex gap-1">
          <Button size="icon" className="rounded-full h-8 w-8 bg-success hover:bg-success/90" onClick={() => act(r.id, "accept")} data-testid={`accept-btn-${r.id}`}><Check className="h-4 w-4" /></Button>
          <Button size="icon" variant="outline" className="rounded-full h-8 w-8" onClick={() => act(r.id, "reject")} data-testid={`reject-btn-${r.id}`}><X className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="font-head font-extrabold text-3xl sm:text-4xl tracking-tight mb-6">Richieste</h1>
      <Tabs defaultValue="received">
        <TabsList className="rounded-full">
          <TabsTrigger value="received" className="rounded-full" data-testid="tab-received"><Inbox className="h-4 w-4 mr-1" /> Ricevute ({received.length})</TabsTrigger>
          <TabsTrigger value="sent" className="rounded-full" data-testid="tab-sent"><Send className="h-4 w-4 mr-1" /> Inviate ({sent.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="received" className="mt-6 space-y-3">
          {received.length === 0 ? <p className="text-muted-foreground text-center py-10">Nessuna richiesta ricevuta.</p> : received.map((r) => <Row key={r.id} r={r} mine={false} />)}
        </TabsContent>
        <TabsContent value="sent" className="mt-6 space-y-3">
          {sent.length === 0 ? <p className="text-muted-foreground text-center py-10">Non hai inviato richieste.</p> : sent.map((r) => <Row key={r.id} r={r} mine={true} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
